import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import {
  CalculatedScore,
  Score,
  Scores,
  UserProfile,
  UserProfileState,
} from "./user-profile.type";

const initialState: UserProfileState = {
  beta: false,
  scores: {
    allIds: [],
    byId: {},
  },
};

export const userProfileSlice = createSlice({
  name: "profile",
  initialState: initialState,
  reducers: {
    setProfile: (state, action: PayloadAction<UserProfile>) => {
      state.beta = !!action.payload.beta;
      state.scores.byId = action.payload.scores;
      state.scores.allIds = Object.keys(action.payload.scores);
    },
    resetProfile: () => {
      return initialState;
    },
  },
});

export const { setProfile, resetProfile } = userProfileSlice.actions;

export default userProfileSlice.reducer;

const calculateResult = (rawScore: Score[]): CalculatedScore => {
  return (rawScore ? rawScore : []).reduce(
    (acc, score) => {
      const diff = score.total - score.correct;
      if (diff === 0) {
        acc.perfect++;
      } else if (diff >= 1 && diff <= 2) {
        acc.good++;
      } else {
        acc.bad++;
      }
      return acc;
    },
    {
      perfect: 0,
      good: 0,
      bad: 0,
    }
  );
};

export const selectCategoryScoreFactory = (ids: string[]) =>
  createSelector(
    (state: RootState): Scores => state.userProfileState.scores,
    (scoreState: Scores): CalculatedScore => {
      return calculateResult(
        ids.flatMap((id) => scoreState.byId[id]).filter((value) => value)
      );
    }
  );

export const selectScoreFactory = (id: string) =>
  createSelector(
    (state: RootState): Scores => state.userProfileState.scores,
    (scoreState: Scores): string[] => {
      const rawScore = scoreState.byId[id];
      const results = calculateResult(rawScore);

      const perfect = [...Array(results.perfect)].map(
        () => "fa fa-trophy text-white"
      );
      const good = [...Array(results.good)].map(
        () => "fas fa-medal text-white"
      );
      const bad = [...Array(results.bad)].map(
        () => "fa fa-poo text-yellow-700"
      );
      const notFilled = [...Array(5)].map(() => "far fa-star text-white");
      return [...perfect, ...good, ...bad, ...notFilled].slice(0, 5).reverse();
    }
  );
